import { Fragment, useId, useLayoutEffect, useRef, useState, type DragEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronDown, ChevronUp, GripVertical, MoreHorizontal, Plus } from 'lucide-react'

import { useAnnounce } from '@/shared/providers/announcer-provider'
import { apiErrorMessage } from '@/lib/api-error-message'
import { cn } from '@/lib/utils'
import { Button } from '@/ui/button'
import { rowMenuTrigger } from '@/ui/button-variants'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLinkItem, DropdownMenuTrigger,
} from '@/ui/dropdown-menu'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { StatusBadge, type StatusTone } from '@/ui/status-badge'
import { Switch } from '@/ui/switch'
import { ActionsTh, Th } from '@/ui/data-table'
import { policyCatalogKeys, type PolicySummary } from '../../api/policy-catalog-api'
import { WHITELABEL_POLICY_TYPE } from '../../api/whitelabel-policy-api'
import { useMovePolicy, usePolicyOrder, useSetPolicyEnabled } from './policy-order-queries'
import type { PolicyRowExpansion, PolicyRowIdentity } from './policy-kind'

const refreshBadges: Record<PolicySummary['refreshStatus'], { label: string; tone: StatusTone; description: string }> = {
  Current: { label: 'Ready', tone: 'success', description: 'Background reconciliation is complete for all policies in this policy type.' },
  Pending: { label: 'Updating', tone: 'warning', description: 'Background reconciliation is still updating policies in this policy type.' },
  Running: { label: 'Updating', tone: 'warning', description: 'Background reconciliation is still updating policies in this policy type.' },
  Failed: { label: 'Update failed', tone: 'destructive', description: 'Background reconciliation failed for a policy in this policy type and needs recovery.' },
  Unknown: { label: 'Status unavailable', tone: 'neutral', description: 'Background reconciliation has not been confirmed for every policy in this policy type. Ready requires a complete list of current policies.' },
}

function groupRefreshStatus(policies: PolicySummary[], complete: boolean): PolicySummary['refreshStatus'] {
  if (policies.some((policy) => policy.refreshStatus === 'Failed')) return 'Failed'
  if (policies.some((policy) => policy.refreshStatus === 'Unknown')) return 'Unknown'
  const updating = policies.some((policy) => policy.refreshStatus === 'Pending' || policy.refreshStatus === 'Running')
  if (updating) return 'Pending'
  if (!complete || policies.length === 0) return 'Unknown'
  return 'Current'
}

type DropTarget = { id: string; after: boolean }

const subjectLabels: Record<string, string> = {
  [WHITELABEL_POLICY_TYPE]: 'Whitelabel',
}

export function PolicyOrderGroup({
  entityId,
  valueType,
  policies,
  canEdit,
  canReadHistory = false,
  complete,
  reloading,
  onEdit,
  onDelete,
  label: providedLabel,
  composition = 'replace',
  grouped = false,
  onCreate,
  expanded: controlledExpanded,
  onExpandedChange,
  selectorSummary,
  expansion,
  resolvedPolicy,
}: {
  entityId: string
  valueType: string
  policies: PolicySummary[]
  canEdit: boolean
  canReadHistory?: boolean
  complete: boolean
  reloading: boolean
  onEdit: (policy: PolicySummary, trigger: HTMLButtonElement) => void
  onDelete: (policy: PolicySummary, trigger: HTMLButtonElement) => void
  label?: string
  composition?: 'replace' | 'additive'
  grouped?: boolean
  onCreate?: () => void
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  selectorSummary?: (policy: PolicyRowIdentity) => string
  expansion?: PolicyRowExpansion
  resolvedPolicy?: PolicyRowIdentity
}) {
  const contentId = useId()
  const [localExpanded, setLocalExpanded] = useState(true)
  const expanded = controlledExpanded ?? localExpanded
  const queryClient = useQueryClient()
  const announce = useAnnounce()
  const hasPeers = policies.length > 1
  const unlistedPolicy = resolvedPolicy && !policies.some((policy) => policy.id === resolvedPolicy.id) ? resolvedPolicy : undefined
  const rows: (PolicySummary | PolicyRowIdentity)[] = unlistedPolicy
    ? [...policies, unlistedPolicy].sort((a, b) => a.priority - b.priority) : policies
  const order = usePolicyOrder(entityId, valueType, canEdit && hasPeers)
  const move = useMovePolicy(entityId, valueType)
  const toggle = useSetPolicyEnabled(entityId, valueType)
  const pendingFocus = useRef<HTMLButtonElement | null>(null)
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null)
  const label = providedLabel ?? subjectLabels[valueType] ?? valueType
  const busy = reloading || move.isPending || toggle.isPending
  const orderMatchesList = order.data !== undefined && order.data.length === policies.length
    && order.data.every((entry, index) => entry.id === policies[index]?.id && entry.revision === policies[index]?.revision)
  const groupComplete = !unlistedPolicy && (complete || (order.data !== undefined
    && order.data.length === policies.length
    && order.data.every((entry) => policies.some((policy) => policy.id === entry.id))))
  const refreshBadge = refreshBadges[groupRefreshStatus(policies, groupComplete)]
  const orderingEnabled = canEdit && hasPeers && groupComplete && orderMatchesList
    && !order.isFetching && !order.isError && !busy && !move.isError && !unlistedPolicy

  useLayoutEffect(() => {
    if (busy || !pendingFocus.current) return
    pendingFocus.current.focus()
    pendingFocus.current = null
  }, [busy, policies])

  function saveMove(policyId: string, beforePolicyId: string | null) {
    if (!order.data || !orderingEnabled) return
    const currentIndex = order.data.findIndex((policy) => policy.id === policyId)
    if (currentIndex < 0 || (order.data[currentIndex + 1]?.id ?? null) === beforePolicyId) return
    move.mutate({
      policyId,
      placement: {
        beforePolicyId,
        expectedOrder: order.data.map(({ id, revision }) => ({ id, revision })),
      },
    }, { onSuccess: () => announce('Policy order saved.') })
  }

  function moveOne(policyId: string, direction: 'up' | 'down', button: HTMLButtonElement) {
    if (!order.data || !orderingEnabled) return
    pendingFocus.current = button
    const index = order.data.findIndex((policy) => policy.id === policyId)
    const previousPolicy = order.data[index - 1]
    if (direction === 'up' && previousPolicy) saveMove(policyId, previousPolicy.id)
    if (direction === 'down' && index >= 0 && index < order.data.length - 1)
      saveMove(policyId, order.data[index + 2]?.id ?? null)
  }

  function dragOver(event: DragEvent<HTMLTableRowElement>, policyId: string) {
    if (!draggedId || !orderingEnabled || draggedId === policyId) return
    event.preventDefault()
    const bounds = event.currentTarget.getBoundingClientRect()
    setDropTarget({ id: policyId, after: event.clientY > bounds.top + bounds.height / 2 })
  }

  function drop(event: DragEvent, targetId: string) {
    event.preventDefault()
    if (draggedId && draggedId !== targetId && order.data && dropTarget?.id === targetId) {
      const remaining = order.data.filter((policy) => policy.id !== draggedId)
      const targetIndex = remaining.findIndex((policy) => policy.id === targetId)
      if (targetIndex >= 0) {
        const insertionIndex = targetIndex + (dropTarget.after ? 1 : 0)
        saveMove(draggedId, remaining[insertionIndex]?.id ?? null)
      }
    }
    setDraggedId(null)
    setDropTarget(null)
  }

  async function reloadOrder() {
    await Promise.all([
      order.refetch(),
      queryClient.invalidateQueries({ queryKey: policyCatalogKeys.all(entityId) }),
    ])
    move.reset()
  }

  return (
    <section className="flex flex-col gap-3" aria-label={`${label} policy order`}>
      <div className={cn('flex flex-col gap-1', grouped && 'bg-muted/40 px-3 py-2')}>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-medium">
            {grouped ? (
              <button type="button" aria-expanded={expanded} aria-controls={contentId}
                className="focus-visible:ring-ring inline-flex items-center gap-2 rounded-sm outline-none focus-visible:ring-2"
                onClick={() => {
                  setLocalExpanded(!expanded)
                  onExpandedChange?.(!expanded)
                }}>
                <ChevronDown aria-hidden className={cn('size-4', !expanded && '-rotate-90')} />
                {label}
              </button>
            ) : label}
          </h2>
          {grouped && <span className="text-muted-foreground whitespace-nowrap text-xs tabular-nums">
            {rows.length}{!groupComplete && '+'} {rows.length === 1 ? 'policy' : 'policies'}
          </span>}
          <StatusBadge tone={refreshBadge.tone} title={refreshBadge.description}>
            {refreshBadge.label}
          </StatusBadge>
          {onCreate && canEdit && <Button type="button" variant="outline" size="sm" className="ml-auto"
            disabled={busy} onClick={onCreate}>
            <Plus aria-hidden />Create policy
          </Button>}
        </div>
        {hasPeers && (
          <p className="text-muted-foreground text-xs">
            {composition === 'additive'
              ? 'All matching policies apply within this scope.'
              : 'The first matching policy wins within this scope.'}
          </p>
        )}
      </div>
      <div id={contentId} hidden={grouped && !expanded} className="flex flex-col gap-3">
      {canEdit && hasPeers && !groupComplete && (
        <p className="text-muted-foreground text-xs">Load all policies to change their order.</p>
      )}
      {canEdit && hasPeers && groupComplete && order.data && !orderMatchesList && !busy && !move.isError && (
        <RetryNotice
          message="The policy list changed. Reload it before reordering."
          buttonLabel="Reload policy order"
          onRetry={() => void reloadOrder()}
        />
      )}
      {order.isError && (
        <RetryNotice
          message="Couldn’t load the policy order."
          buttonLabel="Reload policy order"
          onRetry={() => void reloadOrder()}
        />
      )}
      {move.isError && (
        <RetryNotice
          message={apiErrorMessage(
            move.error,
            'Couldn’t confirm the policy order. Reload it before trying again.',
            { codes: { POLICY_ORDER_CONFLICT: 'The policy order changed. Reload it before trying again.' } },
          )}
          buttonLabel="Reload policy order"
          onRetry={() => void reloadOrder()}
        />
      )}
      {move.isPending && <LoadingNotice message="Saving policy order…" />}
      {toggle.isError && <RetryNotice
        message={apiErrorMessage(toggle.error, 'Couldn’t change the policy. Reload the list before trying again.')}
        buttonLabel="Reload policies"
        onRetry={() => void reloadOrder().then(() => toggle.reset())}
      />}
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-160 table-fixed border-collapse text-left text-xs" aria-label={`${label} policies`}>
          <colgroup>
            <col className="w-24" />
            <col className={grouped ? 'w-16' : 'w-20'} />
            <col className="w-1/3" />
            <col />
            {!grouped && <col className="w-24" />}
            <col className="w-12" />
          </colgroup>
          <thead className={cn('border-border text-muted-foreground border-b', grouped && 'bg-muted/60')}>
            {grouped ? <tr>
              <Th className="whitespace-nowrap px-2">Order</Th>
              <Th className="whitespace-nowrap px-2">Priority</Th>
              <Th>Policy</Th>
              <Th>Selector</Th>
              <ActionsTh />
            </tr> : <tr>
              <th className="p-2">{canEdit && hasPeers && <span className="sr-only">Reorder</span>}</th>
              <th className="p-2 font-medium">Priority</th>
              <th className="py-2 pr-4 font-medium">Policy</th>
              <th className="p-2 font-medium">Applies to</th>
              <th className="p-2 font-medium">Enabled</th>
              <th className="p-2">{(canEdit || canReadHistory) && <span className="sr-only">Actions</span>}</th>
            </tr>}
          </thead>
          <tbody>
            {grouped && rows.length === 0 && <tr>
              <td colSpan={5} className="text-muted-foreground px-6 py-8 text-center">
                {complete ? 'No policies of this type at this entity.' : 'The policy list is not complete.'}
              </td>
            </tr>}
            {rows.map((policy, index) => {
              const selectorText = selectorSummary?.(policy) ?? policy.selectorExpression ?? 'All entities in scope'
              const summary = 'revision' in policy ? policy : undefined
              const disabled = summary?.enabled === false
              const selected = expansion?.policyId === policy.id
              const detailsId = `${contentId}-${policy.id}`
              const enablement = summary?.supportsEnablement ? <Switch aria-label={`Enable ${policy.name}`} checked={summary.enabled}
                disabled={!canEdit || busy || toggle.isError}
                onCheckedChange={(enabled) => toggle.mutate({
                  policyId: policy.id, expectedRevision: summary.revision, enabled,
                }, { onSuccess: () => announce(`${policy.name} ${enabled ? 'enabled' : 'disabled'}. Policies are updating.`) })} /> : null
              return (
              <Fragment key={policy.id}>
              <tr
                onClick={expansion ? (event) => {
                  if (event.detail === 0 || draggedId || !(event.target instanceof Element)
                    || event.target.closest('button, a, input, [role="switch"], [role="menuitem"], [role="menu"]')) return
                  expansion.onSelect(selected ? undefined : policy.id)
                } : undefined}
                onDragOver={(event) => dragOver(event, policy.id)}
                onDrop={(event) => drop(event, policy.id)}
                className={cn(
                  'border-border border-b',
                  expansion && 'cursor-pointer hover:bg-muted',
                  selected && 'bg-muted/40',
                  draggedId === policy.id && 'opacity-50',
                  dropTarget?.id === policy.id
                    && (dropTarget.after ? 'border-b-primary border-b-2' : 'border-t-primary border-t-2'),
                )}
              >
                <td className="p-1">
                  {canEdit && hasPeers && summary && (
                    <div className="flex items-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Drag to reorder ${policy.name}`}
                        draggable={orderingEnabled}
                        disabled={!orderingEnabled}
                        className="cursor-grab active:cursor-grabbing"
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = 'move'
                          event.dataTransfer.setData('text/plain', policy.id)
                          setDraggedId(policy.id)
                        }}
                        onDragEnd={() => {
                          setDraggedId(null)
                          setDropTarget(null)
                        }}
                      >
                        <GripVertical aria-hidden />
                      </Button>
                      <div className={grouped ? 'flex flex-col' : 'contents'}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Move ${policy.name} up`}
                          disabled={!orderingEnabled || index === 0}
                          focusableWhenDisabled
                          onClick={(event) => moveOne(policy.id, 'up', event.currentTarget)}
                        >
                          <ChevronUp aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Move ${policy.name} down`}
                          disabled={!orderingEnabled || index === policies.length - 1}
                          focusableWhenDisabled
                          onClick={(event) => moveOne(policy.id, 'down', event.currentTarget)}
                        >
                          <ChevronDown aria-hidden />
                        </Button>
                      </div>
                    </div>
                  )}
                </td>
                <td className="whitespace-nowrap p-2 tabular-nums">{unlistedPolicy ? policy.priority + 1 : index + 1}</td>
                <th scope="row" aria-label={expansion ? policy.name : undefined} className="py-3 pr-4 font-medium">
                  {expansion ? <button type="button"
                    aria-label={`${selected ? 'Collapse' : 'Expand'} ${policy.name}`}
                    aria-expanded={selected} aria-controls={detailsId}
                    className="focus-visible:ring-ring flex w-full min-w-0 items-center gap-2 rounded-sm text-left outline-none focus-visible:ring-2"
                    onClick={() => expansion.onSelect(selected ? undefined : policy.id)}>
                    <ChevronDown aria-hidden className={cn('size-4 shrink-0', !selected && '-rotate-90')} />
                    <span className={cn('min-w-0 truncate', disabled && 'line-through')} title={policy.name}>{policy.name}</span>
                  </button> : <span
                    className={cn('block min-w-0 truncate', disabled && 'line-through')}
                    title={policy.name}
                  >
                    {policy.name}
                  </span>}
                  {grouped && enablement}
                </th>
                <td className="p-2">
                  <span
                    className={cn(
                      'block min-w-0 truncate',
                      policy.selectorExpression === selectorText && 'font-mono',
                      disabled && 'line-through',
                    )}
                    title={selectorText}
                  >
                    {selectorText}
                  </span>
                </td>
                {!grouped && <td className="p-2">
                  {enablement ?? <span className="text-muted-foreground">Not available</span>}
                </td>}
                <td className="p-2">
                  {summary && (canEdit || canReadHistory) && (
                    <PolicyActions entityId={entityId} policy={summary} canEdit={canEdit}
                      canReadHistory={canReadHistory} busy={busy} onEdit={onEdit} onDelete={onDelete} />
                  )}
                </td>
              </tr>
              {expansion && <tr hidden={!selected}>
                <td colSpan={grouped ? 5 : 6} className="p-2">
                  <section id={detailsId} aria-label={`Details for ${policy.name}`}
                    className="border-border bg-muted/15 rounded-md border border-l-2 border-l-primary p-6">
                    {selected && expansion.renderDetails(policy)}
                  </section>
                </td>
              </tr>}
              </Fragment>
            )})}
          </tbody>
        </table>
      </div>
      </div>
    </section>
  )
}

function PolicyActions({ entityId, policy, canEdit, canReadHistory, busy, onEdit, onDelete }: {
  entityId: string
  policy: PolicySummary
  canEdit: boolean
  canReadHistory: boolean
  busy: boolean
  onEdit: (policy: PolicySummary, trigger: HTMLButtonElement) => void
  onDelete: (policy: PolicySummary, trigger: HTMLButtonElement) => void
}) {
  const trigger = useRef<HTMLButtonElement | null>(null)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger ref={trigger} className={rowMenuTrigger} aria-label={`Actions for ${policy.name}`}>
        <MoreHorizontal aria-hidden className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {canReadHistory && (
          <DropdownMenuLinkItem closeOnClick render={<Link to="/$entityId/audit-log" params={{ entityId }} search={{ q: policy.id }} />}
            aria-label={`History for ${policy.name}`}>
            History
          </DropdownMenuLinkItem>
        )}
        {canEdit && (
          <DropdownMenuItem disabled={busy} aria-label={`Edit ${policy.name}`}
            onClick={() => {
              if (trigger.current) onEdit(policy, trigger.current)
            }}>
            Edit
          </DropdownMenuItem>
        )}
        {canEdit && (
          <DropdownMenuItem disabled={busy} variant="destructive" aria-label={`Delete ${policy.name}`}
            onClick={() => {
              if (trigger.current) onDelete(policy, trigger.current)
            }}>
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
